import type { PurchaseOrderInvoice, PurchaseOrderInvoiceLineDetails, PurchaseOrderInvoiceLines } from "@shared/types/payout";

export interface DemoInvoicesOptions {
  /** ISO currency of the roaster's region, e.g. "EUR" */
  currencyCode: string;
  /** Countries the roaster ships to; more than one makes the Ship to column show per-order countries */
  shipToCountries: readonly string[];
  /** Adds the Kaffeesteuer amount to each order line (German-region roasters only) */
  includeKaffeesteuer: boolean;
  /** Anchor for the relative invoice dates; injectable for tests */
  now?: Date;
}

interface DemoInvoiceSeed {
  status: PurchaseOrderInvoice["invoiceStatus"];
  daysAgo: number;
  orderAmounts: number[];
}

const DAY_MS = 86_400_000;
const PAYMENT_TERM_DAYS = 30;
const VAT_RATE = 10;
const SHIPPING_FEE = 12;

const DEMO_INVOICE_SEEDS: readonly DemoInvoiceSeed[] = [
  { status: "Unposted", daysAgo: 3, orderAmounts: [120, 85.5] },
  { status: "Unpaid", daysAgo: 12, orderAmounts: [240, 180, 96.5] },
  { status: "Paid", daysAgo: 45, orderAmounts: [310, 150.25, 99, 72] },
  { status: "Paid", daysAgo: 75, orderAmounts: [205, 133] },
];

const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);

const round2 = (value: number): number => Math.round(value * 100) / 100;

const buildLine = (
  invoiceIndex: number,
  lineIndex: number,
  orderAmount: number,
  options: DemoInvoicesOptions,
  invoiceDate: Date
): PurchaseOrderInvoiceLines => {
  const orderDate = new Date(invoiceDate.getTime() - (lineIndex + 2) * DAY_MS);
  const shipDate = new Date(orderDate.getTime() + DAY_MS);
  const orderAmountBeforeTax = round2(orderAmount / (1 + VAT_RATE / 100));
  const shipToCountry = options.shipToCountries[lineIndex % Math.max(options.shipToCountries.length, 1)];
  return {
    purchaseOrderId: `DEMO-INV-PO-${invoiceIndex + 1}${lineIndex + 1}`,
    orderDate: toIsoDate(orderDate),
    shipDate: toIsoDate(shipDate),
    purchaseOrderStatus: "Delivered",
    orderAmount,
    shippingFee: SHIPPING_FEE,
    vatRate: VAT_RATE,
    vatAmount: round2(orderAmount - orderAmountBeforeTax),
    orderAmountBeforeTax,
    ...(options.includeKaffeesteuer ? { ksAmount: round2(orderAmountBeforeTax * 0.02) } : {}),
    ...(shipToCountry ? { shipToCountry, taxJurisdiction: shipToCountry } : {}),
  };
};

/** Sample invoices, newest first, with the order lines the detail drawer lists */
export const buildDemoInvoices = (options: DemoInvoicesOptions): PurchaseOrderInvoice[] => {
  const now = options.now ?? new Date();
  return DEMO_INVOICE_SEEDS.map((seed, invoiceIndex) => {
    const invoiceDate = new Date(now.getTime() - seed.daysAgo * DAY_MS);
    const invoiceLines = seed.orderAmounts.map((amount, lineIndex) => buildLine(invoiceIndex, lineIndex, amount, options, invoiceDate));
    return {
      invoiceId: `DEMO-INV-${1000 + invoiceIndex + 1}`,
      invoiceDate: toIsoDate(invoiceDate),
      invoiceAmount: round2(invoiceLines.reduce((sum, line) => sum + line.orderAmount + line.shippingFee, 0)),
      invoiceStatus: seed.status,
      poCount: invoiceLines.length,
      lastSettleDate: seed.status === "Paid" ? toIsoDate(new Date(invoiceDate.getTime() + 7 * DAY_MS)) : "",
      dueDate: toIsoDate(new Date(invoiceDate.getTime() + PAYMENT_TERM_DAYS * DAY_MS)),
      currencyCode: options.currencyCode,
      invoiceLines,
    };
  });
};

/** The items on one sample order, which the drawer loads when the roaster expands that order */
export const buildDemoLineDetails = (
  invoices: readonly PurchaseOrderInvoice[],
  purchaseOrderId: string
): PurchaseOrderInvoiceLineDetails[] => {
  const invoice = invoices.find((candidate) => candidate.invoiceLines.some((line) => line.purchaseOrderId === purchaseOrderId));
  const line = invoice?.invoiceLines.find((candidate) => candidate.purchaseOrderId === purchaseOrderId);
  if (!invoice || !line) return [];
  const itemAmount = round2(line.orderAmount / 2);
  const base = {
    invoiceId: invoice.invoiceId,
    invoiceDate: invoice.invoiceDate,
    invoiceStatus: invoice.invoiceStatus,
    shipDate: line.shipDate,
    purchaseOrderId,
  };
  return [
    { ...base, approvedAmount: itemAmount, quantity: 2, itemName: "Single Origin Ethiopia 340g", itemNumber: "DEMO-SKU-1" },
    { ...base, approvedAmount: round2(line.orderAmount - itemAmount), quantity: 1, itemName: "House Blend 1kg", itemNumber: "DEMO-SKU-2" },
  ];
};
